/* eslint react-hooks/exhaustive-deps: "warn" */

import { Fragment, useCallback, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './CtaSection.module.scss';

import { type CtaSectionProps } from './CtaSection.types';

// How much of the remaining distance the spotlight covers each frame (0–1). Lower = smoother/slower glide.
const EASE = 0.12;

export const CtaSection = ({
  kicker,
  title,
  description,
  actions,
  keywords,
  bottom,
  theme = 'light',
  layout = 'centered',
  card = true,
  cardBackground,
  grid = true,
  glow = false,
  spotlight = false,
  animate = false,
  className,
}: CtaSectionProps) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const target = useRef<{ x: number; y: number } | null>(null);
  const current = useRef<{ x: number; y: number } | null>(null);
  const active = useRef(false);
  const rafId = useRef<number | undefined>(undefined);

  const revealed = useIntersection(innerRef, { threshold: 0.3, when: animate });

  const restingPos = (el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    return { x: rect.width / 2, y: rect.height * 0.3 };
  };

  const tick = useCallback(() => {
    const el = innerRef.current;
    if (!el || !target.current) {
      rafId.current = undefined;
      return;
    }

    if (!current.current) {
      current.current = restingPos(el);
    }

    const dx = target.current.x - current.current.x;
    const dy = target.current.y - current.current.y;
    current.current.x += dx * EASE;
    current.current.y += dy * EASE;

    el.style.setProperty('--x', `${current.current.x}px`);
    el.style.setProperty('--y', `${current.current.y}px`);

    if (!active.current && Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
      target.current = null;
      current.current = null;
      rafId.current = undefined;
      return;
    }

    rafId.current = requestAnimationFrame(tick);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = innerRef.current;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    target.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    active.current = true;
    el.classList.add(styles.active);
    if (rafId.current === undefined) {
      rafId.current = requestAnimationFrame(tick);
    }
  };

  const handleMouseLeave = () => {
    const el = innerRef.current;
    if (!el) {
      return;
    }

    active.current = false;
    target.current = restingPos(el);
    el.classList.remove(styles.active);
    if (rafId.current === undefined) {
      rafId.current = requestAnimationFrame(tick);
    }
  };

  useEffect(
    () => () => {
      if (rafId.current !== undefined) {
        cancelAnimationFrame(rafId.current);
      }
    },
    [],
  );

  const copy = (
    <TitleBlock
      extra={kicker}
      title={title}
      description={description}
      theme={theme}
      accent="blue"
      align={layout === 'split' ? 'left' : 'center'}
      className={styles.titleBlock}
    />
  );

  return (
    <section className={cn(styles.section, styles[`theme_${theme}`], styles[`layout_${layout}`], className)}>
      <div
        ref={innerRef}
        className={cn(styles.inner, card && styles.cardSurface)}
        style={cardBackground ? { background: cardBackground } : undefined}
        onMouseMove={spotlight ? handleMouseMove : undefined}
        onMouseLeave={spotlight ? handleMouseLeave : undefined}
      >
        {spotlight && <div className={styles.spotlightOverlay} aria-hidden="true" />}
        {grid && <div className={styles.grid} aria-hidden="true" />}
        {glow && <div className={styles.glow} aria-hidden="true" />}
        {spotlight && (
          <>
            <div className={styles.blobA} aria-hidden="true" />
            <div className={styles.blobB} aria-hidden="true" />
          </>
        )}

        <div className={cn(styles.content, animate && styles.animate, revealed && styles.revealed)}>
          {layout === 'split' ? (
            <>
              <div className={styles.copy}>{copy}</div>
              {actions && <div className={styles.actions}>{actions}</div>}
            </>
          ) : (
            <>
              {copy}
              {actions && <div className={styles.actions}>{actions}</div>}
            </>
          )}
        </div>

        {!!keywords?.length && (
          <div className={styles.trust}>
            {keywords.map((keyword, index) => (
              <Fragment key={keyword}>
                {index > 0 && <i />}
                <span>{keyword}</span>
              </Fragment>
            ))}
          </div>
        )}

        {bottom}
      </div>
    </section>
  );
};
